import React from "react";
import { Box, Paper, Typography, ToggleButton, ToggleButtonGroup, IconButton, Tooltip, TextField, Chip, Button, Link } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import { BarPlot } from "@mui/x-charts/BarChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import api from "../Auth/Api";
import globals from "../Globals";
import { navigate, replaceDeep } from "../Layout/Router";
import { PageContainer, SectionHeader, DataTable, SelectorBar, SelectField, formatDate, relTime, preWrap } from "../Layout/Common";

// System Health → Errors: the server error log (backend ErrorLog, collection errorLogs, 30 days). Rows are grouped by
// fingerprint (same service/module/action/exception/message/throw site); a group opens its stack trace and latest
// occurrences. Deep link: /health/errors/<fingerprint>.

const REFRESH_MS = 60000;
const RANGES = [{ value: 1, label: "1h" }, { value: 24, label: "24h" }, { value: 72, label: "3d" }, { value: 168, label: "7d" }, { value: 720, label: "30d" }];
const FILTERS = [
    { key: "titleId", facet: "titles", label: "Game" },
    { key: "service", facet: "services", label: "Service" },
    { key: "module", facet: "modules", label: "Module" },
    { key: "action", facet: "actions", label: "Action" },
    { key: "branch", facet: "branches", label: "Branch" },
    { key: "version", facet: "versions", label: "Version" },
    { key: "type", facet: "types", label: "Exception" },
];
const EMPTY_FILTERS = { titleId: "", service: "", module: "", action: "", branch: "", version: "", type: "", search: "", playerId: "" };
const FIXED = { "& table": { tableLayout: "fixed" } };
const ELLIPSIS = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const num = (v) => Number(v ?? 0).toLocaleString();
const titleName = (id) => {
    const t = globals.loginData?.titles?.titles?.[id];
    return t?.title ?? t?.name ?? id;
};
const shortType = (type) => (type ?? "").split(".").pop();

export default class ServerErrorsPage extends React.Component {
    state = {
        hours: 24, filters: { ...EMPTY_FILTERS }, data: null, loadedAt: null,
        selected: globals.route?.deep?.[0] ?? null, occurrences: null,
    };

    componentDidMount() {
        this.load();
        this.timer = setInterval(() => this.load(), REFRESH_MS);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    query = () => {
        const { hours, filters } = this.state;
        const q = { hours };
        for (const [k, v] of Object.entries(filters)) if (v !== "") q[k] = v;
        return q;
    }

    load = () => {
        api.health("Loading server errors", ["errors"], { payload: this.query(), silent: true })
            .then(r => { if (r && r.groups) this.setState({ data: r, loadedAt: Date.now() }); })
            .catch(() => {});
        if (this.state.selected) this.loadOccurrences(this.state.selected);
    }

    loadOccurrences = (fingerprint) => {
        api.health("Loading occurrences", ["error-occurrences"], { payload: { ...this.query(), fingerprint, limit: 50 }, silent: true })
            .then(r => { if (r && r.rows && this.state.selected === fingerprint) this.setState({ occurrences: r.rows }); })
            .catch(() => {});
    }

    setRange = (e, hours) => {
        if (hours == null) return;
        this.setState({ hours }, this.load);
    }

    setFilter = (key, value) => this.setState({ filters: { ...this.state.filters, [key]: value } }, this.load);

    // Text filters apply on Enter or blur, not per keystroke.
    textFilter = (key, label, width) => (
        <TextField size="small" label={label} sx={{ width }} defaultValue={this.state.filters[key]} key={key + "-" + this.state.filtersNonce}
                   onKeyDown={e => { if (e.key === "Enter") this.setFilter(key, e.target.value.trim()); }}
                   onBlur={e => { if (e.target.value.trim() !== this.state.filters[key]) this.setFilter(key, e.target.value.trim()); }} />
    );

    clearFilters = () => this.setState({ filters: { ...EMPTY_FILTERS }, filtersNonce: (this.state.filtersNonce ?? 0) + 1 }, this.load);

    select = (fingerprint) => {
        this.setState({ selected: fingerprint, occurrences: null });
        replaceDeep(fingerprint ? [fingerprint] : []);
        if (fingerprint) this.loadOccurrences(fingerprint);
    }

    openPlayer = (titleId, playerId) => navigate({ title: titleId, page: "home", sub: "players", deep: [playerId] });

    timeLabel = (t) => {
        const d = new Date(t);
        const hm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
        return this.state.hours > 24 ? `${d.getMonth() + 1}/${d.getDate()} ${hm}` : hm;
    }

    renderFilters() {
        const facets = this.state.data?.facets ?? {};
        const { filters } = this.state;
        const active = Object.values(filters).some(v => v !== "");
        return (
            <SelectorBar sx={{ flexWrap: "wrap" }}>
                {FILTERS.map(f => (
                    <SelectField key={f.key} size="small" label={f.label} minWidth={f.key === "type" ? 200 : 130} value={filters[f.key]}
                                 onChange={v => this.setFilter(f.key, v)}
                                 options={[{ value: "", label: "All" }, ...(facets[f.facet] ?? []).map(v => ({
                                     value: v, label: f.key === "titleId" ? titleName(v) : f.key === "type" ? shortType(v) : v,
                                 }))]} />
                ))}
                {this.textFilter("search", "Message contains", 200)}
                {this.textFilter("playerId", "Player id", 230)}
                <Button size="small" onClick={this.clearFilters} disabled={!active}>Clear</Button>
            </SelectorBar>
        );
    }

    renderChart() {
        const series = this.state.data.series;
        return (
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Errors over time ({this.state.data.binMin} min buckets)
                </Typography>
                {series.length === 0
                    ? <Typography variant="body2" color="text.secondary">No errors in this range.</Typography>
                    : <ChartContainer height={180}
                                      xAxis={[{ id: "t", scaleType: "band", data: series.map(s => this.timeLabel(s.t)), tickLabelStyle: { fontSize: 10 } }]}
                                      yAxis={[{ id: "n" }]}
                                      series={[{ type: "bar", id: "count", yAxisId: "n", data: series.map(s => s.count), label: "Errors", color: "#d32f2f" }]}>
                        <BarPlot />
                        <ChartsXAxis axisId="t" />
                        <ChartsYAxis axisId="n" />
                        <ChartsTooltip />
                    </ChartContainer>}
            </Paper>
        );
    }

    renderGroups() {
        const { groups } = this.state.data;
        const columns = [
            { id: "lastSeen", label: "Last seen", width: "11%", render: g => <Tooltip title={`first ${formatDate(g.firstSeen)} · last ${formatDate(g.lastSeen)}`}><span>{relTime(g.lastSeen)}</span></Tooltip> },
            { id: "count", label: "Count", width: "7%", align: "right", render: g => <strong>{num(g.count)}</strong> },
            { id: "players", label: "Players", width: "7%", align: "right", render: g => num(g.players) },
            { id: "titleId", label: "Game", width: "9%", sx: ELLIPSIS, render: g => titleName(g.titleId) },
            { id: "where", label: "Module · action", width: "14%", sx: ELLIPSIS, render: g => <Tooltip title={`service ${g.service}`}><span>{g.module} · {g.actionName}</span></Tooltip> },
            { id: "error", label: "Error", width: "38%", render: g => (
                <Link component="button" underline="hover" onClick={() => this.select(g.fingerprint)} sx={{ textAlign: "left", display: "block", width: "100%" }}>
                    <Typography variant="body2" sx={{ fontWeight: this.state.selected === g.fingerprint ? 700 : 500, ...ELLIPSIS }}>{shortType(g.type)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", ...ELLIPSIS }}>{g.message}</Typography>
                </Link>
            ) },
            { id: "versions", label: "Branches · versions", width: "14%", render: g => (
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {g.branches.map(b => <Chip key={"b" + b} size="small" label={b} />)}
                    {g.versions.map(v => <Chip key={"v" + v} size="small" variant="outlined" label={v} />)}
                </Box>
            ) },
        ];
        return groups.length === 0
            ? <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>No errors match these filters.</Typography>
            : <DataTable columns={columns} rows={groups} getRowKey={g => g.fingerprint} sx={{ ...FIXED, mb: 2 }} />;
    }

    renderDetail() {
        const { selected, occurrences, data } = this.state;
        if (!selected) return null;
        const group = data?.groups?.find(g => g.fingerprint === selected);
        const latest = occurrences?.[0];
        const columns = [
            // one group = one game and one action (both are part of the fingerprint), so the rows carry only what varies
            { id: "t", label: "Time", width: "17%", render: r => <Tooltip title={relTime(r.t)}><span>{formatDate(r.t)}</span></Tooltip> },
            { id: "player", label: "Player", width: "25%", sx: ELLIPSIS, render: r => (
                <Link component="button" underline="hover" onClick={() => this.openPlayer(r.titleId, r.playerId)} sx={{ fontFamily: "monospace", fontSize: 12 }}>{r.playerId}</Link>
            ) },
            { id: "branch", label: "Branch", width: "13%", sx: ELLIPSIS, render: r => r.branch },
            { id: "version", label: "Version", width: "10%", render: r => r.version },
            { id: "country", label: "Country", width: "13%", sx: ELLIPSIS, render: r => r.countryName },
            { id: "instance", label: "Instance", width: "12%", sx: ELLIPSIS, render: r => r.instance },
            { id: "suppressed", label: "Throttled", width: "10%", align: "right", render: r => r.suppressed ? `+${num(r.suppressed)}` : "—" },
        ];
        return (
            <Paper sx={{ p: 2, mb: 2, textAlign: "left" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{latest?.type ?? group?.type ?? selected}</Typography>
                        <Typography variant="body2" sx={preWrap}>{latest?.message ?? group?.message}</Typography>
                        {group && <Typography variant="caption" color="text.secondary">
                            {num(group.count)} occurrences · {num(group.players)} players · first {formatDate(group.firstSeen)} · last {formatDate(group.lastSeen)} · fingerprint {selected}
                        </Typography>}
                    </Box>
                    <Tooltip title="Close"><IconButton size="small" onClick={() => this.select(null)}><CloseIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
                {!occurrences
                    ? <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading…</Typography>
                    : occurrences.length === 0
                        ? <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>No occurrences in this range with these filters.</Typography>
                        : <>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>Stack trace (latest occurrence)</Typography>
                            <Box component="pre" sx={{ m: 0, mb: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1, fontSize: 12, overflowX: "auto", maxHeight: 360 }}>
                                {latest.stack}
                            </Box>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Latest occurrences ({occurrences.length})</Typography>
                            <DataTable columns={columns} rows={occurrences} getRowKey={r => r.id} sx={FIXED} />
                        </>}
            </Paper>
        );
    }

    render() {
        const { data, hours, loadedAt } = this.state;
        return (
            <PageContainer maxWidth={1400}>
                <SectionHeader title="Server Errors">
                    {data && <Typography variant="caption" color="text.secondary">{num(data.total)} errors</Typography>}
                    {loadedAt && <Typography variant="caption" color="text.secondary">updated {relTime(loadedAt)}</Typography>}
                    <ToggleButtonGroup size="small" exclusive value={hours} onChange={this.setRange}>
                        {RANGES.map(r => <ToggleButton key={r.value} value={r.value}>{r.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                    <Tooltip title="Refresh"><IconButton size="small" onClick={this.load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </SectionHeader>
                {this.renderFilters()}
                {!data
                    ? <Typography variant="body2" color="text.secondary">Loading…</Typography>
                    : <>
                        {this.renderChart()}
                        {this.renderDetail()}
                        {this.renderGroups()}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                            Unexpected exceptions of the modules that report here (today: SDLB), kept 30 days. Up to 10 rows per error per
                            instance per minute are stored; the rest are counted as throttled and included in the counts.
                        </Typography>
                    </>}
            </PageContainer>
        );
    }
}
